import React, { useEffect, useState } from "react";
import { Box, FormControl, Select, MenuItem } from "@mui/material";

const ZoomDropdown = ({ scale, setScale }) => {
  // Docusign-style zoom options - minimum 100% to prevent positioning issues
  const zoomOptions = [
    { value: "default", label: "Default" },
    { value: 1, label: "100%" },
    { value: 1.25, label: "125%" },
    { value: 1.5, label: "150%" },
    { value: 2, label: "200%" },
    { value: "fitToWidth", label: "Fit to width" },
  ];

  // Get display value for the dropdown
  const getDisplayValue = (currentScale) => {
    // If it's already "default" or "fitToWidth", return as-is
    if (currentScale === "default" || currentScale === "fitToWidth") {
      return currentScale;
    }
    if (typeof currentScale === "number") {
      // Find the exact matching option
      const exactMatch = zoomOptions.find(opt => opt.value === currentScale);
      if (exactMatch) return exactMatch.value;

      // If no exact match, find the closest valid option to prevent MUI warnings
      const numericOptions = zoomOptions.filter(opt => typeof opt.value === "number");
      const closestOption = numericOptions.reduce((prev, curr) => 
        Math.abs(curr.value - currentScale) < Math.abs(prev.value - currentScale) ? curr : prev
      );
      return closestOption.value;
    }
    return 1; // Fallback to 100%
  };

  // Get display text for current selection
  const getDisplayText = (currentScale) => {
    if (currentScale === "default") {
      return "Default";
    }
    if (currentScale === "fitToWidth") {
      return "Fit to width";
    }
    if (typeof currentScale === "number") {
      return `${Math.round(currentScale * 100)}%`;
    }
    return "100%";
  };

  // Handle zoom change
  const handleZoomChange = (event) => {
    const value = event.target.value;

    if (value === "default") {
      // Calculate optimal scale to fit container (same as auto-fit on load)
      const pdfContainer = document.querySelector(".pdf-container");
      const firstPage = pdfContainer?.querySelector(".pdf-page-container");
      
      if (firstPage && pdfContainer) {
        const containerWidth = pdfContainer.offsetWidth;
        const pageWidth = firstPage.scrollWidth;
        
        if (pageWidth < containerWidth * 0.7) {
          const calculatedScale = Math.min(1.25, (containerWidth * 0.9) / pageWidth);
          // Round to nearest valid zoom option: 1, 1.25, 1.5, 2 (minimum 100%)
          const validOptions = [1, 1.25, 1.5, 2];
          const nearestOption = validOptions.reduce((prev, curr) =>
            Math.abs(curr - calculatedScale) < Math.abs(prev - calculatedScale) ? curr : prev
          );
          setScale(Math.max(1, nearestOption));
        } else {
          setScale(1);
        }
      } else {
        setScale(1);
      }
    } else if (value === "fitToWidth") {
      // Calculate fit to width scale (minimum 100% to prevent positioning issues)
      const pdfContainer = document.querySelector(".pdf-container");
      const pdfImage = document.querySelector(".pdf-image");

      if (pdfContainer && pdfImage) {
        const containerWidth = pdfContainer.clientWidth;
        const imageWidth = pdfImage.naturalWidth || pdfImage.width;
        if (imageWidth > 0) {
          const fitScale = containerWidth / imageWidth;
          // Ensure minimum scale is 1 (100%)
          setScale(Math.max(1, fitScale));
        }
      }
    } else {
      // Set the numeric scale value
      setScale(parseFloat(value));
    }
  };

  return (
    <Box sx={{ minWidth: "100px" }}>
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={getDisplayValue(scale)}
          onChange={handleZoomChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === "default") {
              return "Default";
            }
            if (selected === "fitToWidth") {
              return "Fit to width";
            }
            if (typeof selected === "number") {
              return `${Math.round(selected * 100)}%`;
            }
            // If scale doesn't match any option, show the actual scale
            if (typeof scale === "number") {
              return `${Math.round(scale * 100)}%`;
            }
            return "100%";
          }}
          sx={{
            "& .MuiSelect-select": {
              py: 0.75,
              px: 1.5,
              fontSize: "14px",
              border: "1px solid rgba(33, 150, 243, 0.5)",
              borderRadius: "4px",
              "&:focus": {
                border: "1px solid rgb(33, 150, 243)",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid rgba(33, 150, 243, 0.5)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "1px solid rgb(33, 150, 243)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid rgb(33, 150, 243)",
            },
          }}
        >
          {zoomOptions.map((option) => {
            const isSelected =
              (option.value === "default" && scale === "default") ||
              (option.value === "fitToWidth" && scale === "fitToWidth") ||
              (typeof option.value === "number" && typeof scale === "number" &&
                Math.abs(option.value - scale) < 0.01);

            return (
              <MenuItem
                key={option.value}
                value={option.value}
                selected={isSelected}
                sx={{
                  fontSize: "14px",
                  py: 1,
                  "&:hover": {
                    backgroundColor: "rgba(33, 150, 243, 0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(33, 150, 243, 0.16)",
                    color: "rgb(33, 150, 243)",
                    "&:hover": {
                      backgroundColor: "rgba(33, 150, 243, 0.24)",
                    },
                  },
                }}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ZoomDropdown;
// import React, { useEffect, useState } from "react";
// import { Box, FormControl, Select, MenuItem } from "@mui/material";

// const ZoomDropdown = ({ scale, setScale, originalWidth, renderedWidth }) => {
//   const [baseScale, setBaseScale] = useState(1);

//   const calculateBaseScale = () => {
//     if (originalWidth && renderedWidth) {
//       return renderedWidth / originalWidth;
//     }
//     return 1;
//   };

//   useEffect(() => {
//     const initialScale = calculateBaseScale();
//     setBaseScale(initialScale);
//     setScale(initialScale);
//   }, [originalWidth, renderedWidth]);

//   const zoomOptions = [
//     { value: "fit", label: "Fit to Screen" },
//     { value: "fill", label: "Fill to Screen" },
//     { value: 1, label: "100%" },
//     { value: 0.75, label: "75%" },
//     { value: 0.5, label: "50%" },
//   ];

//   const handleZoomChange = (event) => {
//     const value = event.target.value;

//     if (value === "fit") {
//       const fitScale = calculateBaseScale();
//       setScale(fitScale);
//     } else if (value === "fill") {
//       setScale(1); // Fill to natural size
//     } else {
//       setScale(parseFloat(value));
//     }
//   };

//   return (
//     <Box sx={{ width: "100%", p: 2 }}>
//       <FormControl fullWidth size="small">
//         <Select
//           value={scale}
//           onChange={handleZoomChange}
//           displayEmpty
//           sx={{
//             width: "100%",
//             "& .MuiSelect-select": {
//               py: 1,
//               fontSize: "14px",
//             },
//           }}
//         >
//           {zoomOptions.map((option) => (
//             <MenuItem
//               key={option.value}
//               value={option.value}
//               sx={{
//                 fontSize: "14px",
//                 py: 1,
//                 "&:hover": { backgroundColor: "rgba(0, 145, 255, 0.08)" },
//                 "&.Mui-selected": {
//                   backgroundColor: "rgba(0, 145, 255, 0.16)",
//                   "&:hover": { backgroundColor: "rgba(0, 145, 255, 0.24)" },
//                 },
//               }}
//             >
//               {option.label}
//             </MenuItem>
//           ))}
//         </Select>
//       </FormControl>
//     </Box>
//   );
// };

// export default ZoomDropdown;
